import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { apiClient } from './api';

/**
 * Sign-in, both ways.
 *
 * Supabase Auth is the real one: the browser talks to GoTrue, gets a JWT, and
 * hands it to our API, which verifies it against Supabase's public keys. No
 * password ever reaches our server.
 *
 * The dev path exists so the whole application — RLS, tenancy, entitlements,
 * the lab — can be run and tested without a Supabase project. It is refused by
 * the server outside development, so this is not a second way in so much as a
 * way to work offline from a cloud dependency.
 */

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

let supabase: SupabaseClient | null = null;
if (url && anonKey) {
  supabase = createClient(url, anonKey, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
  });
}

export const supabaseConfigured = Boolean(supabase);

export async function signInWithPassword(email: string, password: string): Promise<void> {
  if (!supabase) throw new Error('Supabase sign-in is not configured');
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw new Error(error.message);
  if (!data.session) throw new Error('No session returned');
  apiClient.setUserToken(data.session.access_token);
}

export async function signUpWithPassword(
  email: string,
  password: string,
  fullName: string,
): Promise<{ needsConfirmation: boolean }> {
  if (!supabase) throw new Error('Supabase sign-in is not configured');
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { full_name: fullName } },
  });
  if (error) throw new Error(error.message);

  if (data.session) {
    apiClient.setUserToken(data.session.access_token);
    return { needsConfirmation: false };
  }
  // Email confirmation is on. The account exists; there is nothing to hand the
  // API until they click the link, and saying so is better than a spinner.
  return { needsConfirmation: true };
}

export async function signInWithGoogle(): Promise<void> {
  if (!supabase) throw new Error('Supabase sign-in is not configured');
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: `${window.location.origin}/auth/callback` },
  });
  if (error) throw new Error(error.message);
}

/** Development sign-in. The API refuses it unless DEV_AUTH_ENABLED is on. */
export async function signInWithDevEmail(email: string, fullName?: string): Promise<void> {
  const response = await fetch(`${import.meta.env.VITE_API_URL ?? '/api'}/auth/dev/session`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, fullName }),
  });
  const body = (await response.json()) as {
    status: string;
    data?: { token: string };
    message?: string;
  };

  if (!response.ok || body.status !== 'success' || !body.data?.token) {
    throw new Error(body.message ?? 'Development sign-in is disabled on this server');
  }
  apiClient.setUserToken(body.data.token);
}

/**
 * Restores a Supabase session on load.
 *
 * Called once at boot. Without it, a refresh drops the user to the login screen
 * even though GoTrue still holds a valid session in local storage.
 */
export async function restoreSession(): Promise<boolean> {
  if (!supabase) return Boolean(apiClient.getUserToken());
  const { data } = await supabase.auth.getSession();
  if (data.session) {
    apiClient.setUserToken(data.session.access_token);
    return true;
  }
  return Boolean(apiClient.getUserToken());
}

export async function signOut(): Promise<void> {
  await supabase?.auth.signOut();
  apiClient.clear();
}

export interface AuthMethods {
  supabase: boolean;
  devEmail: boolean;
  /** Named identities a demo deployment invites visitors to assume. */
  demoAccounts: string[];
}

/** Which methods this deployment actually offers, so the login page is honest. */
export async function authMethods(): Promise<AuthMethods> {
  try {
    return await apiClient.open<AuthMethods>('/auth/methods');
  } catch {
    return { supabase: supabaseConfigured, devEmail: false, demoAccounts: [] };
  }
}
